import { useState } from "react";

// ─── Design Tokens (matches Kubernetes TLS page theme) ───────────────────────
const COLORS = {
  bg: "#0a0e1a",
  bgCard: "#111827",
  bgCardAlt: "#1a2236",
  border: "#1e293b",
  text: "#e2e8f0",
  textMuted: "#94a3b8",
  textDim: "#64748b",
  accent: "#00D4FF",
  accentGlow: "rgba(0,212,255,0.15)",
  success: "#34D399",
  successGlow: "rgba(52,211,153,0.15)",
  warning: "#FBBF24",
  warningGlow: "rgba(251,191,36,0.15)",
  danger: "#F87171",
  dangerGlow: "rgba(248,113,113,0.15)",
  purple: "#A78BFA",
  purpleGlow: "rgba(167,139,250,0.15)",
  orange: "#FB923C",
};

const FONT = `'JetBrains Mono','Fira Code','SF Mono',monospace`;
const FONT_BODY = `'Inter','DM Sans','Segoe UI',system-ui,sans-serif`;

const layout = {
  app: { background: COLORS.bg, minHeight: "100vh", color: COLORS.text, fontFamily: FONT_BODY, fontSize: 14 },
  sidebar: { width: 240, background: COLORS.bgCard, borderRight: `1px solid ${COLORS.border}`, height: "calc(100vh - 70px)", position: "fixed", top: 70, left: 0, overflowY: "auto", zIndex: 100 },
  main: { marginLeft: 240, minHeight: "100vh", padding: "32px 40px", maxWidth: 1000 },
  navItem: (active, color) => ({ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", cursor: "pointer", borderRadius: 8, margin: "2px 8px", background: active ? `${color}18` : "transparent", color: active ? color : COLORS.textMuted, fontWeight: active ? 600 : 400, fontSize: 13, transition: "all .18s", borderLeft: active ? `3px solid ${color}` : "3px solid transparent", animation: active ? "signal-pulse 2s ease-in-out infinite" : "none" }),
  badge: (col) => ({ display: "inline-block", padding: "2px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700, background: col + "22", color: col, border: `1px solid ${col}44`, letterSpacing: 0.3 }),
};

// ─── Global styles for content (pills, callouts, ascii boxes) ────────────────
const FONT_IMPORT = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;600&display=swap');

@keyframes signal-pulse {
  0%   { box-shadow: 0 0 0px 0px rgba(0,212,255,0); }
  50%  { box-shadow: 0 0 12px 4px rgba(0,212,255,0.35); }
  100% { box-shadow: 0 0 0px 0px rgba(0,212,255,0); }
}
@keyframes flow {
  0%   { stroke-dashoffset: 200; }
  100% { stroke-dashoffset: 0; }
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
}
.fade-in { animation: fadeIn 0.35s ease forwards; }

.mono { font-family: 'JetBrains Mono', monospace; }

.pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  font-family: 'JetBrains Mono', monospace;
}
.pill-cyan   { background: rgba(0,212,255,0.12); color: #00D4FF; border: 1px solid rgba(0,212,255,0.25); }
.pill-amber  { background: rgba(251,191,36,0.12); color: #FBBF24; border: 1px solid rgba(251,191,36,0.25); }
.pill-green  { background: rgba(52,211,153,0.12); color: #34D399; border: 1px solid rgba(52,211,153,0.25); }
.pill-purple { background: rgba(167,139,250,0.12); color: #A78BFA; border: 1px solid rgba(167,139,250,0.25); }
.pill-red    { background: rgba(248,113,113,0.12); color: #F87171; border: 1px solid rgba(248,113,113,0.25); }

.callout {
  border-left: 3px solid;
  padding: 10px 14px;
  border-radius: 0 8px 8px 0;
  font-size: 13.5px;
  line-height: 1.6;
  margin: 10px 0;
}
.callout-exam   { border-color: #FBBF24; background: rgba(251,191,36,0.07); }
.callout-tip    { border-color: #34D399; background: rgba(52,211,153,0.07); }
.callout-warn   { border-color: #F87171; background: rgba(248,113,113,0.07); }
.callout-info   { border-color: #00D4FF; background: rgba(0,212,255,0.07); }
.callout-purple { border-color: #A78BFA; background: rgba(167,139,250,0.07); }

.ascii-box {
  background: #030912;
  border: 1px solid #1E3A5F;
  border-radius: 10px;
  padding: 16px 20px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  line-height: 1.7;
  color: #7DD3FC;
  overflow-x: auto;
  white-space: pre;
}
.ascii-box .c  { color: #00D4FF; font-weight: 600; } /* cyan highlight */
.ascii-box .g  { color: #34D399; }                   /* green */
.ascii-box .a  { color: #FBBF24; }                   /* amber */
.ascii-box .p  { color: #A78BFA; }                   /* purple */
.ascii-box .r  { color: #F87171; }                   /* red */
.ascii-box .w  { color: #CBD5E1; }                   /* white-ish */
`;

// ─── Data ────────────────────────────────────────────────────────────────────

const TABS = [
  { id: "jumbo",   label: "Jumbo Frames", icon: "📦", color: "#FBBF24" },
  { id: "eni",     label: "ENI",          icon: "🔌", color: "#00D4FF" },
  { id: "ena",     label: "ENA",          icon: "⚡", color: "#34D399" },
  { id: "efa",     label: "EFA",          icon: "🚀", color: "#A78BFA" },
  { id: "compare", label: "Compare All",  icon: "⚖️", color: "#F87171" },
  { id: "quiz",    label: "Quiz",         icon: "🎯", color: "#FB923C" },
];

// ─── Sub-components ──────────────────────────────────────────────────────────

function SectionTitle({ children, color = "#00D4FF" }) {
  return (
    <h2 style={{
      fontSize: 22, fontWeight: 700, color,
      borderBottom: `2px solid ${color}22`,
      paddingBottom: 8, marginBottom: 18, marginTop: 32,
      letterSpacing: "-0.02em"
    }}>{children}</h2>
  );
}

function SubTitle({ children }) {
  return (
    <h3 style={{
      fontSize: 15, fontWeight: 600, color: "#94A3B8",
      textTransform: "uppercase", letterSpacing: "0.08em",
      marginTop: 24, marginBottom: 10
    }}>{children}</h3>
  );
}

function Prose({ children }) {
  return <p style={{ fontSize: 14, lineHeight: 1.8, color: "#94A3B8", marginBottom: 12 }}>{children}</p>;
}

function Callout({ type = "info", icon, children }) {
  const icons = { exam: "🎓", tip: "💡", warn: "⚠️", info: "ℹ️", purple: "🔬" };
  return (
    <div className={`callout callout-${type}`}>
      <span style={{ marginRight: 8 }}>{icon || icons[type]}</span>
      {children}
    </div>
  );
}

function Table({ headers, rows, accent = "#00D4FF" }) {
  return (
    <div style={{ overflowX: "auto", marginBottom: 16 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={i} style={{
                padding: "8px 12px", textAlign: "left",
                background: `${accent}18`,
                color: accent, fontWeight: 600,
                borderBottom: `2px solid ${accent}44`,
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 12, whiteSpace: "nowrap"
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ borderBottom: "1px solid #1E3A5F" }}>
              {row.map((cell, j) => (
                <td key={j} style={{
                  padding: "8px 12px", color: j === 0 ? "#CBD5E1" : "#64748B",
                  verticalAlign: "top", fontSize: 13, lineHeight: 1.6
                }}
                  dangerouslySetInnerHTML={{ __html: cell }} />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AsciiBox({ children }) {
  return <div className="ascii-box" dangerouslySetInnerHTML={{ __html: children }} />;
}

// ─── JUMBO FRAMES ────────────────────────────────────────────────────────────
function JumboFrames() {
  return (
    <div className="fade-in">
      <div style={{ marginBottom: 24 }}>
        <span className="pill pill-amber">MTU • Packet Size • Layer 2</span>
        <span className="pill pill-amber" style={{ marginLeft: 8 }}>Introduced: Early AWS (2012+)</span>
      </div>

      <SectionTitle color="#FBBF24">Part 1 — Fundamentals</SectionTitle>

      <div style={{ background: "rgba(251,191,36,0.06)", border: "1px solid rgba(251,191,36,0.2)", borderRadius: 12, padding: 20, marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: "#FBBF24", fontWeight: 700, marginBottom: 8 }}>🚛 THE HIGHWAY TRUCK ANALOGY</div>
        <Prose>
          Imagine sending 9000 packages from Delhi to Mumbai. You can use <strong style={{ color: "#FBBF24" }}>standard trucks (MTU 1500)</strong> — each truck carries 1 box. That's 9000 trips, 9000 times you pay toll, 9000 times the driver checks paperwork. OR, you use <strong style={{ color: "#34D399" }}>jumbo freight trailers (MTU 9001)</strong> — each trailer carries 6× more cargo. Fewer trips, less overhead, same goods delivered faster.
        </Prose>
        <Prose>
          In networking: <strong style={{ color: "#CBD5E1" }}>MTU = Maximum Transmission Unit</strong> = the biggest "box" (frame) your network can send in one shot. Standard Ethernet MTU is <strong style={{ color: "#F87171" }}>1500 bytes</strong>. AWS Jumbo Frames allow <strong style={{ color: "#34D399" }}>9001 bytes</strong> — 6× bigger payload per trip.
        </Prose>
      </div>

      <Table
        accent="#FBBF24"
        headers={["Property", "Standard Frame", "Jumbo Frame"]}
        rows={[
          ["MTU Size", "1500 bytes", "9001 bytes"],
          ["Header overhead", "~40 bytes (TCP/IP)", "~40 bytes (TCP/IP)"],
          ["Payload per frame", "~1460 bytes", "~8961 bytes"],
          ["Frames for 1 MB data", "~700 frames", "~114 frames"],
          ["CPU interrupts", "High (one per frame)", "~6× fewer"],
          ["Latency impact", "More headers processed", "Lower processing overhead"],
          ["Where supported", "Everywhere", "Within same VPC / same region"],
        ]}
      />

      <Callout type="warn" icon="⚠️">
        <strong>FRAGMENTATION TRAP:</strong> If a packet hits a hop that doesn't support Jumbo Frames (e.g., Internet Gateway, VPN, transit to another region), it gets <strong>fragmented or dropped</strong>. Jumbo Frames only work <strong>end-to-end within the same VPC</strong> or between VPCs in the same region via VPC Peering.
      </Callout>

      <SubTitle>Where Jumbo Frames Work in AWS</SubTitle>
      <Table
        accent="#FBBF24"
        headers={["Path", "Jumbo Frames?", "Max MTU", "Notes"]}
        rows={[
          ["Within same VPC", "✅ Yes", "9001", "Full support between EC2 instances"],
          ["VPC Peering (same region)", "✅ Yes", "9001", "Works if both sides use ENA"],
          ["VPC Peering (cross-region)", "⚠️ Partial", "1500", "Limited to 1500 across regions"],
          ["Internet Gateway", "❌ No", "1500", "Internet doesn't support jumbo"],
          ["VPN Connection", "❌ No", "1500", "Encapsulation overhead"],
          ["AWS Direct Connect", "✅ Yes", "9001", "Dedicated line supports jumbo"],
          ["Transit Gateway", "✅ Yes", "8500", "Slightly less than max"],
          ["EFA (HPC)", "✅ Yes", "9001", "Designed for this"],
        ]}
      />

      <SectionTitle color="#FBBF24">Part 2 — Visual Architecture</SectionTitle>

      <SubTitle>Packet Count Comparison</SubTitle>
      <AsciiBox>{`
<span class="a">SENDING 1 MB OF DATA (1,048,576 bytes)</span>

<span class="w">STANDARD MTU 1500  ┌─────────────────────────────────────────────────────────┐</span>
<span class="r">                   │ F│F│F│F│F│F│F│F│F│F│F│F│F│F│F│F│F│F│F│F│F│F│F│F│F│F│F │</span>
<span class="w">                   │ ↑ each box = 1 frame (1500B)       ~700 frames total    │</span>
<span class="w">                   │ CPU interrupts: ~700   Header bytes wasted: ~28,000B    │</span>
<span class="w">                   └─────────────────────────────────────────────────────────┘</span>

<span class="w">JUMBO  MTU 9001    ┌─────────────────────────────────────────────────────────┐</span>
<span class="g">                   │ FRAME  │ FRAME  │ FRAME  │...            ~114 frames    │</span>
<span class="w">                   │ ↑ each box = 1 jumbo frame (9001B)                      │</span>
<span class="w">                   │ CPU interrupts: ~114   Header bytes wasted:  ~4,560B    │</span>
<span class="w">                   └─────────────────────────────────────────────────────────┘</span>

<span class="c">                        6.1× FEWER FRAMES = LESS CPU = LOWER LATENCY</span>
`}</AsciiBox>

      <SubTitle>How MTU Flows Through AWS Network Stack</SubTitle>
      <AsciiBox>{`
<span class="a">EC2 Instance A                                        EC2 Instance B</span>
<span class="w">┌──────────────────────┐                          ┌──────────────────────┐</span>
<span class="w">│  Application         │                          │  Application         │</span>
<span class="w">│  (write 50KB data)   │                          │  (receives data)     │</span>
<span class="w">│         ↓            │                          │         ↑            │</span>
<span class="g">│  OS sets MTU=9001    │                          │  OS reassembles      │</span>
<span class="g">│  (via ip link / ENI) │                          │  jumbo frames        │</span>
<span class="w">│         ↓            │                          │         ↑            │</span>
<span class="c">│  ENA Driver          │                          │  ENA Driver          │</span>
<span class="c">│  (sends 9001B frames)│                          │  (receives 9001B)    │</span>
<span class="w">│         ↓            │                          │         ↑            │</span>
<span class="w">└─────────┬────────────┘                          └──────────┬───────────┘</span>
          │                                                   │
          │         <span class="g">◄─── AWS VPC FABRIC (MTU 9001) ───►</span>        │
          └───────────────────────────────────────────────────┘

<span class="a">⚠️  If path goes through Internet Gateway:</span>
<span class="w">          EC2 ──→ [MTU 9001] ──→ IGW ──→ [FRAGMENTED to 1500] ──→ Internet</span>
<span class="r">                                    ↑ Fragmentation or ICMP "frag needed" drop</span>
`}</AsciiBox>

      <Callout type="exam" icon="🎓">
        <strong>EXAM TRAP:</strong> "Ping works but large file transfer fails between VPCs" — this is a <strong>Path MTU Discovery (PMTUD) / Jumbo Frame fragmentation</strong> issue. ICMP "Fragmentation Needed" packets may be blocked by Security Groups. Fix: ensure ICMP is allowed, or set explicit MTU on OS: <code style={{ color: "#34D399" }}>ip link set dev eth0 mtu 9001</code>
      </Callout>

      <Callout type="tip" icon="💡">
        <strong>K8s / EKS Context:</strong> In Kubernetes on AWS, the CNI plugin (aws-vpc-cni) automatically sets MTU to 9001 for pod-to-pod traffic within the same node. Cross-node traffic between pods also benefits from Jumbo Frames if nodes are ENA-enabled. Always check <code style={{ color: "#34D399" }}>AWS_VPC_K8S_CNI_MTU</code> env variable.
      </Callout>
    </div>
  );
}

// ─── ENI ─────────────────────────────────────────────────────────────────────
function ENI() {
  return (
    <div className="fade-in">
      <div style={{ marginBottom: 24 }}>
        <span className="pill pill-cyan">Virtual NIC • Layer 3 • VPC</span>
        <span className="pill pill-cyan" style={{ marginLeft: 8 }}>Since: AWS VPC launch (2009)</span>
      </div>

      <SectionTitle color="#00D4FF">Part 1 — Fundamentals</SectionTitle>

      <div style={{ background: "rgba(0,212,255,0.06)", border: "1px solid rgba(0,212,255,0.2)", borderRadius: 12, padding: 20, marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: "#00D4FF", fontWeight: 700, marginBottom: 8 }}>🏢 THE OFFICE DESK PHONE ANALOGY</div>
        <Prose>
          Think of an EC2 instance as an <strong style={{ color: "#CBD5E1" }}>employee's desk</strong>. An ENI is like a <strong style={{ color: "#00D4FF" }}>desk telephone</strong> — it has a fixed extension number (private IP), can have multiple lines (secondary IPs), can be transferred to another desk (detachable ENI), and has its own caller-ID (MAC address, Security Group).
        </Prose>
        <Prose>
          A single desk can have <strong>multiple phones</strong> — the primary phone always stays, but you can plug in/unplug secondary phones. If the employee moves desks (instance stop/start), the secondary phone number (ENI) can be <strong>moved to the new desk</strong> — keeping the same number!
        </Prose>
      </div>

      <Table
        accent="#00D4FF"
        headers={["ENI Property", "Detail", "Exam Note"]}
        rows={[
          ["What it is", "Virtual network card for EC2", "Every EC2 has at least 1 ENI (eth0)"],
          ["Primary ENI (eth0)", "Created with instance, deleted with it", "Cannot be detached while instance runs"],
          ["Secondary ENI", "Can be attached/detached hot", "Retains IP/MAC across detach-reattach"],
          ["Private IPs per ENI", "Multiple per ENI", "Instance type limits total IPs"],
          ["Public IP", "1 per private IP (via NAT)", "Auto-assigned or Elastic IP"],
          ["MAC Address", "Unique, stays with ENI", "AWS licenses tied to MAC (e.g., Windows)"],
          ["Security Groups", "Applied to ENI, not instance", "Different ENIs can have different SGs"],
          ["Source/Dest Check", "Can disable per ENI", "Required for NAT instances"],
          ["Subnet", "Fixed to one subnet/AZ", "ENI cannot span AZs"],
          ["Description", "Custom string", "Used for identification/filtering"],
        ]}
      />

      <SectionTitle color="#00D4FF">Part 2 — Visual Architecture</SectionTitle>

      <SubTitle>ENI Anatomy on an EC2 Instance</SubTitle>
      <AsciiBox>{`
<span class="w">┌───────────────────────────────────────────────────────────────────────┐</span>
<span class="w">│                        EC2 INSTANCE (m5.xlarge)                       │</span>
<span class="w">│                                                                       │</span>
<span class="w">│   ┌─────────────────────────────┐  ┌──────────────────────────────┐  │</span>
<span class="c">│   │  PRIMARY ENI (eth0)         │  │  SECONDARY ENI (eth1)        │  │</span>
<span class="w">│   │  ┌─────────────────────┐   │  │  ┌──────────────────────┐   │  │</span>
<span class="g">│   │  │ Private IP: 10.0.1.5│   │  │  │ Private IP: 10.0.2.7 │   │  │</span>
<span class="g">│   │  │ Private IP: 10.0.1.6│   │  │  │ Elastic IP: 54.x.x.x │   │  │</span>
<span class="a">│   │  │ Elastic IP: 3.x.x.x │   │  │  │ MAC: 02:AB:CD:EF:12  │   │  │</span>
<span class="a">│   │  │ MAC: 0A:BC:DE:F0:11 │   │  │  └──────────────────────┘   │  │</span>
<span class="r">│   │  │ Security Groups:     │   │  │  Security Groups:            │  │</span>
<span class="r">│   │  │  - sg-web (80,443)   │   │  │   - sg-mgmt (22,8443)       │  │</span>
<span class="w">│   │  └─────────────────────┘   │  │                              │  │</span>
<span class="w">│   │  Subnet: subnet-prod-1a     │  │  Subnet: subnet-mgmt-1a      │  │</span>
<span class="p">│   │  Source/Dest Check: ON      │  │  Source/Dest Check: OFF      │  │</span>
<span class="w">│   └─────────────────────────────┘  └──────────────────────────────┘  │</span>
<span class="w">│             │                                    │                    │</span>
<span class="w">└─────────────┼────────────────────────────────────┼────────────────────┘</span>
              │                                    │
        <span class="c">VPC Fabric (10.0.1.0/24)</span>         <span class="g">VPC Fabric (10.0.2.0/24)</span>
`}</AsciiBox>

      <SubTitle>ENI Hot-Swap — High Availability Pattern</SubTitle>
      <AsciiBox>{`
<span class="a">SCENARIO: Primary instance fails, failover to standby using ENI move</span>

<span class="w">BEFORE FAILURE:                          AFTER FAILOVER:</span>
<span class="w">┌──────────────┐  ┌──────────────┐      ┌──────────────┐  ┌──────────────┐</span>
<span class="w">│  Instance A  │  │  Instance B  │      │  Instance A  │  │  Instance B  │</span>
<span class="r">│  (PRIMARY)   │  │  (STANDBY)   │      │  (FAILED ✗)  │  │  (NOW ACTIVE)│</span>
<span class="w">│              │  │              │      │              │  │              │</span>
<span class="c">│  eni-abc     │  │  eni-xyz     │      │  eni-abc     │  │  eni-abc ◄──┼──┐</span>
<span class="w">│  10.0.1.100  │  │  10.0.1.200  │      │  (detached)  │  │  10.0.1.100 │  │</span>
<span class="w">└──────────────┘  └──────────────┘      └──────────────┘  └──────────────┘  │</span>
        │                                                                     │
<span class="g">        ↓  Client hits 10.0.1.100                   ENI hot-swapped ─────────┘</span>
<span class="g">        Same IP, zero DNS change, sub-second failover!</span>

<span class="a">Real-world use: NLB targets, NAT instances, licensing servers, DB failover</span>
`}</AsciiBox>

      <SubTitle>ENI Use Cases — Architecture Patterns</SubTitle>
      <AsciiBox>{`
<span class="c">1. DUAL-HOMED NETWORK APPLIANCE (Firewall / NAT)</span>
<span class="w">   Internet ──→ ENI-public (eth0, SG: allow 80/443 from 0.0.0.0/0)</span>
<span class="w">            ──→ [EC2 Firewall]</span>
<span class="w">            ──→ ENI-private (eth1, SG: allow all from 10.0.0.0/8)</span>
<span class="w">            ──→ Private Subnet</span>

<span class="g">2. MANAGEMENT PLANE SEPARATION</span>
<span class="w">   eth0 → Production traffic (SG: app ports only)</span>
<span class="w">   eth1 → SSH bastion / monitoring (SG: port 22 from jump host only)</span>

<span class="a">3. LICENSING SERVER (MAC-locked software)</span>
<span class="w">   ENI eni-lic has MAC 0A:BC:DE:F0:11  ← software license tied to this MAC</span>
<span class="w">   If instance fails → detach ENI → attach to new instance</span>
<span class="w">   MAC stays same → license stays valid!</span>

<span class="p">4. KUBERNETES / EKS NODE</span>
<span class="w">   Node eth0 → primary ENI (node IP, kubelet, system)</span>
<span class="w">   Node eth1 → secondary ENI (pod IPs via aws-vpc-cni)</span>
<span class="w">   Node eth2 → secondary ENI (more pod IPs)</span>
<span class="w">   Max pods = (ENIs - 1) × (IPs per ENI - 1) + 2</span>
`}</AsciiBox>

      <Callout type="exam" icon="🎓">
        <strong>EXAM QUESTION:</strong> "An EC2 instance runs licensed software. The instance needs to be replaced. How do you keep the license valid?" → Answer: The license is MAC-bound. <strong>Detach the secondary ENI (with its MAC) and attach it to the new instance.</strong>
      </Callout>

      <Callout type="exam" icon="🎓">
        <strong>EXAM TRAP:</strong> Security Groups are attached to ENIs, NOT to instances. If you want different rules for different network interfaces on the same instance, use multiple ENIs with different Security Groups.
      </Callout>

      <Callout type="tip" icon="💡">
        <strong>EKS Max Pods Formula:</strong> Number of ENIs the instance supports × (IPv4 per ENI − 1) + 2. Example: m5.large supports 3 ENIs × 10 IPs = 30 − 3 + 2 = <strong>29 max pods</strong>.
      </Callout>
    </div>
  );
}

// ─── ENA ─────────────────────────────────────────────────────────────────────
function ENA() {
  return (
    <div className="fade-in">
      <div style={{ marginBottom: 24 }}>
        <span className="pill pill-green">SR-IOV • High Throughput • Driver</span>
        <span className="pill pill-green" style={{ marginLeft: 8 }}>Introduced: 2015 (C4, M4)</span>
      </div>

      <SectionTitle color="#34D399">Part 1 — Fundamentals</SectionTitle>

      <div style={{ background: "rgba(52,211,153,0.06)", border: "1px solid rgba(52,211,153,0.2)", borderRadius: 12, padding: 20, marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: "#34D399", fontWeight: 700, marginBottom: 8 }}>🏎️ THE RACING LANE ANALOGY</div>
        <Prose>
          Traditional virtualized networking is like a <strong style={{ color: "#F87171" }}>highway with one toll booth for all lanes</strong>. Every car (packet) must stop at the hypervisor toll booth, pay overhead, and continue. The toll booth is the bottleneck.
        </Prose>
        <Prose>
          ENA with <strong style={{ color: "#34D399" }}>SR-IOV (Single Root I/O Virtualization)</strong> gives each VM its own <strong>dedicated FastPass lane that bypasses the toll booth</strong>. The physical NIC is divided into Virtual Functions (VFs), and each EC2 instance gets direct hardware access — <strong>no hypervisor middleman for data-plane packets</strong>.
        </Prose>
      </div>

      <Table
        accent="#34D399"
        headers={["Property", "Traditional vNIC", "ENA (SR-IOV)"]}
        rows={[
          ["Data path", "App → OS → Hypervisor → Physical NIC", "App → OS → Physical NIC (direct)"],
          ["Hypervisor involvement", "Every packet processed by hypervisor", "Control plane only (setup, not data)"],
          ["Max throughput", "~10 Gbps (older gen)", "Up to 100 Gbps (c5n, p4d, etc.)"],
          ["Latency", "~100–200µs", "~10–25µs"],
          ["CPU overhead", "High (hypervisor calls)", "Low (direct hardware path)"],
          ["PPS (Packets/sec)", "Limited by vNIC overhead", "Up to 14.88 Mpps"],
          ["Bandwidth", "Up to 25 Gbps (most instances)", "Up to 100 Gbps (enhanced networking)"],
          ["OS support", "All OS", "Amazon Linux 2, RHEL, Ubuntu, etc."],
        ]}
      />

      <SubTitle>ENA Capabilities</SubTitle>
      <Table
        accent="#34D399"
        headers={["Feature", "Description", "Benefit"]}
        rows={[
          ["Enhanced Networking", "SR-IOV-based high performance NIC", "Higher PPS, lower latency, lower CPU"],
          ["Multiple Tx/Rx Queues", "Parallel data lanes (up to 32)", "Scales with CPU cores, no bottleneck"],
          ["RSS (Receive Side Scaling)", "Spreads receive traffic across CPUs", "No single-core bottleneck"],
          ["LRO / GRO", "Large Receive Offload", "Coalesces packets, reduces CPU"],
          ["Checksum Offload", "TCP/UDP checksum done in hardware", "CPU freed for application work"],
          ["Adaptive Interrupt", "Coalesces interrupts when busy", "Reduces CPU context switches"],
          ["Jumbo Frame Support", "MTU up to 9001", "Large data transfers more efficient"],
          ["ENA Express", "Newer: SRD protocol, lower tail latency", "Best for latency-sensitive apps"],
        ]}
      />

      <SectionTitle color="#34D399">Part 2 — Visual Architecture</SectionTitle>

      <SubTitle>Traditional vNIC vs ENA Data Path</SubTitle>
      <AsciiBox>{`
<span class="r">TRADITIONAL VIRTUALIZED NETWORKING (Software vNIC)</span>
<span class="w">┌──────────────────────────────────────────────────────────────┐</span>
<span class="w">│  EC2 Guest OS                                                │</span>
<span class="w">│  ┌───────────┐                                               │</span>
<span class="w">│  │Application│ → write() → TCP/IP Stack → virtio NIC driver  │</span>
<span class="w">│  └───────────┘                                 │             │</span>
<span class="w">└───────────────────────────────────────────────↓─────────────┘</span>
<span class="r">                                         VM EXIT (trap to hypervisor)</span>
<span class="r">                                                 │</span>
<span class="r">┌──────────────────────────────────────────────↓─────────────┐</span>
<span class="r">│  HYPERVISOR (processes every packet - BOTTLENECK!)          │</span>
<span class="r">│  Virtual Switch → emulated NIC → physical driver            │</span>
<span class="r">└────────────────────────────────────────────────────────────┘</span>
                                                 ↓
                                        Physical Network

<span class="g">ENA (SR-IOV): DIRECT HARDWARE PATH</span>
<span class="w">┌──────────────────────────────────────────────────────────────┐</span>
<span class="w">│  EC2 Guest OS                                                │</span>
<span class="w">│  ┌───────────┐                                               │</span>
<span class="w">│  │Application│ → write() → TCP/IP Stack → ENA driver         │</span>
<span class="w">│  └───────────┘                                 │             │</span>
<span class="w">└───────────────────────────────────────────────↓─────────────┘</span>
<span class="g">                              Direct PCIe Access (no VM exit for data!)</span>
<span class="g">                                                 │</span>
<span class="g">┌──────────────────────────────────────────────↓─────────────┐</span>
<span class="g">│  Physical NIC (Virtual Function assigned to this instance)  │</span>
<span class="g">│  Hardware: multi-queue, RSS, checksum offload, Jumbo frames  │</span>
<span class="g">└────────────────────────────────────────────────────────────┘</span>
                                                 ↓
<span class="g">                                        Physical Network (fast!)</span>

<span class="a">Hypervisor is only involved for CONTROL PLANE (setup/teardown), not DATA PLANE</span>
`}</AsciiBox>

      <SubTitle>SR-IOV: One Physical NIC → Many Virtual Functions</SubTitle>
      <AsciiBox>{`
<span class="w">                    Physical NIC (100 Gbps)</span>
<span class="w">                    ┌──────────────────────────────────────────┐</span>
<span class="w">                    │        Physical Function (PF)            │</span>
<span class="c">                    │  ┌──────┐ ┌──────┐ ┌──────┐ ┌──────┐   │</span>
<span class="c">                    │  │ VF-1 │ │ VF-2 │ │ VF-3 │ │ VF-4 │   │</span>
<span class="c">                    │  └──┬───┘ └──┬───┘ └──┬───┘ └──┬───┘   │</span>
<span class="w">                    └─────┼────────┼────────┼────────┼────────┘</span>
                          │        │        │        │
<span class="g">               EC2 Inst-A  EC2 Inst-B  EC2 Inst-C  EC2 Inst-D</span>
<span class="g">               (25 Gbps)   (25 Gbps)   (25 Gbps)   (25 Gbps)</span>

<span class="a">Each EC2 instance sees VF as its own dedicated NIC.</span>
<span class="a">No hypervisor mediation for packet I/O. Isolation maintained by hardware.</span>
`}</AsciiBox>

      <SubTitle>ENA Express — Next Generation</SubTitle>
      <AsciiBox>{`
<span class="c">ENA EXPRESS (SRD - Scalable Reliable Datagram)</span>
<span class="w">┌─────────────────────────────────────────────────────────────────┐</span>
<span class="w">│  PROBLEM with standard TCP in cloud:                            │</span>
<span class="r">│  Tail latency spikes due to head-of-line blocking in TCP        │</span>
<span class="w">│                                                                 │</span>
<span class="w">│  SOLUTION - SRD (AWS custom transport):                         │</span>
<span class="g">│  • Spreads packets across MULTIPLE PATHS simultaneously         │</span>
<span class="g">│  • Out-of-order delivery at hardware level                      │</span>
<span class="g">│  • Hardware-based packet spray across all available paths       │</span>
<span class="g">│  • Reorders at destination before presenting to application     │</span>
<span class="w">│                                                                 │</span>
<span class="a">│  RESULT: p99 latency reduced by up to 85% vs standard ENA      │</span>
<span class="a">│          Available on: C6gn, C6in, Hpc6a, P4d instances        │</span>
<span class="w">└─────────────────────────────────────────────────────────────────┘</span>
`}</AsciiBox>

      <Callout type="exam" icon="🎓">
        <strong>EXAM KEY:</strong> ENA is the <em>driver/adapter</em>. Enhanced Networking is the <em>feature</em>. You enable Enhanced Networking by using an instance type that supports ENA and having the ENA driver installed. Check with: <code style={{ color: "#34D399" }}>aws ec2 describe-instances --query "...EnaSupport"</code>
      </Callout>

      <Callout type="tip" icon="💡">
        <strong>Instance Support:</strong> All current-gen instances (C5, M5, R5, P3+, etc.) support ENA by default. Older gen (C3, M3) use Intel 82599 VF (ixgbevf) instead.
      </Callout>
    </div>
  );
}

// ─── EFA ─────────────────────────────────────────────────────────────────────
function EFA() {
  return (
    <div className="fade-in">
      <div style={{ marginBottom: 24 }}>
        <span className="pill pill-purple">RDMA • MPI • HPC • ML Training</span>
        <span className="pill pill-purple" style={{ marginLeft: 8 }}>Introduced: 2019 (re:Invent 2018)</span>
      </div>

      <SectionTitle color="#A78BFA">Part 1 — Fundamentals</SectionTitle>

      <div style={{ background: "rgba(167,139,250,0.06)", border: "1px solid rgba(167,139,250,0.2)", borderRadius: 12, padding: 20, marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: "#A78BFA", fontWeight: 700, marginBottom: 8 }}>🚀 THE DIRECT TELEPATHY ANALOGY</div>
        <Prose>
          Normal networking (even ENA) is like <strong style={{ color: "#CBD5E1" }}>workers passing notes through a manager</strong>: Worker A writes note → gives to manager → manager routes to Worker B → Worker B reads. Even if fast, the manager (OS kernel) is always in the loop.
        </Prose>
        <Prose>
          EFA is like <strong style={{ color: "#A78BFA" }}>workers who can read each other's whiteboards directly</strong> — no manager needed. One GPU/CPU can directly read/write another machine's memory <strong>without involving the remote OS at all</strong>. This is <strong>RDMA (Remote Direct Memory Access)</strong>.
        </Prose>
        <Prose>
          Why does it matter? For 1000-GPU training jobs, all GPUs must synchronize gradients millions of times. Even a 1ms OS overhead × 1000 nodes × millions of iterations = <strong>days of wasted compute</strong>. EFA eliminates that overhead.
        </Prose>
      </div>

      <Table
        accent="#A78BFA"
        headers={["Property", "ENA", "EFA"]}
        rows={[
          ["Purpose", "General-purpose high-perf networking", "HPC / ML / tightly-coupled workloads"],
          ["Protocol", "TCP/UDP (kernel involved)", "OS-Bypass + libfabric (kernel bypassed for data)"],
          ["Latency", "~10–25µs", "~1–4µs (MPI workloads)"],
          ["Throughput", "Up to 100 Gbps", "Up to 400 Gbps (p4de)"],
          ["MPI support", "Works but with OS overhead", "Native, optimized (Open MPI, Intel MPI)"],
          ["RDMA", "No", "Yes (via SRD, OS-bypass)"],
          ["Use case", "Web, databases, microservices", "HPC, ML training, genomics, CFD, rendering"],
          ["OS involvement", "Full kernel network stack", "Bypassed for data plane (libfabric/verbs)"],
          ["Placement Group", "Optional", "Required (cluster placement group)"],
          ["Supported instances", "All current-gen", "P4d, P3dn, C5n, Hpc6a, Inf1, Trn1..."],
        ]}
      />

      <SubTitle>Key Supported Instance Families</SubTitle>
      <Table
        accent="#A78BFA"
        headers={["Instance", "EFA Bandwidth", "Primary Use"]}
        rows={[
          ["p4d.24xlarge", "400 Gbps (4× 100G EFA)", "ML training (A100 GPUs)"],
          ["p4de.24xlarge", "400 Gbps", "ML training + HBM GPUs"],
          ["trn1.32xlarge", "800 Gbps", "AWS Trainium (LLM training)"],
          ["hpc6a.48xlarge", "100 Gbps EFA", "HPC workloads (AMD EPYC)"],
          ["c5n.18xlarge", "100 Gbps EFA", "Network-intensive HPC"],
          ["p3dn.24xlarge", "100 Gbps EFA", "ML training (V100 GPUs)"],
        ]}
      />

      <SectionTitle color="#A78BFA">Part 2 — Visual Architecture</SectionTitle>

      <SubTitle>Traditional MPI vs EFA MPI Communication</SubTitle>
      <AsciiBox>{`
<span class="r">TRADITIONAL MPI (over TCP/IP, even on ENA)</span>
<span class="w">Node A                                           Node B</span>
<span class="w">┌────────────────────────────┐  ┌────────────────────────────┐</span>
<span class="w">│  MPI Process               │  │  MPI Process               │</span>
<span class="w">│       ↓ MPI_Send           │  │  MPI_Recv ↑                │</span>
<span class="w">│  libfabric / OpenMPI       │  │  libfabric / OpenMPI       │</span>
<span class="r">│       ↓ copy to kernel     │  │  copy from kernel ↑        │</span>
<span class="r">│   OS Kernel (TCP stack)    │  │  OS Kernel (TCP stack)     │</span>
<span class="r">│       ↓ sys call           │  │  interrupt ↑               │</span>
<span class="w">│   ENA Driver               │  │  ENA Driver                │</span>
<span class="w">└────────────────────────────┘  └────────────────────────────┘</span>
                  │<span class="r">  (latency: 10-50µs + jitter)</span>           ↑
                  └──────────────────────────────────────────┘

<span class="g">EFA MPI (OS-bypass via libfabric)</span>
<span class="w">Node A                                           Node B</span>
<span class="w">┌────────────────────────────┐  ┌────────────────────────────┐</span>
<span class="w">│  MPI Process               │  │  MPI Process               │</span>
<span class="w">│       ↓ MPI_Send           │  │  MPI_Recv ↑                │</span>
<span class="g">│  libfabric (EFA provider)  │  │  libfabric (EFA provider)  │</span>
<span class="g">│       ↓ DIRECT DMA         │  │  DMA directly into mem ↑   │</span>
<span class="g">│   EFA Hardware             │  │  EFA Hardware              │</span>
<span class="g">│   (OS completely bypassed) │  │  (OS completely bypassed)  │</span>
<span class="w">└────────────────────────────┘  └────────────────────────────┘</span>
                  │<span class="g">  (latency: 1-4µs, no jitter)</span>          ↑
                  └──────────────────────────────────────────┘
`}</AsciiBox>

      <SubTitle>EFA HPC Cluster Architecture</SubTitle>
      <AsciiBox>{`
<span class="w">         CLUSTER PLACEMENT GROUP (same physical rack/switch)</span>
<span class="w">         ┌──────────────────────────────────────────────────────┐</span>
<span class="w">         │                                                      │</span>
<span class="p">         │  ┌────────────┐  ┌────────────┐  ┌────────────┐    │</span>
<span class="p">         │  │ p4d.24xl   │  │ p4d.24xl   │  │ p4d.24xl   │    │</span>
<span class="w">         │  │ 8× A100    │  │ 8× A100    │  │ 8× A100    │    │</span>
<span class="g">         │  │ 400G EFA   │  │ 400G EFA   │  │ 400G EFA   │    │</span>
<span class="w">         │  └─────┬──────┘  └─────┬──────┘  └─────┬──────┘    │</span>
<span class="w">         │        │               │               │            │</span>
<span class="w">         │        └───────┬───────┘               │            │</span>
<span class="w">         │                │                       │            │</span>
<span class="c">         │         EFA Fabric Switch (non-blocking, low latency) │</span>
<span class="w">         │                │                       │            │</span>
<span class="w">         │        ┌───────┴───────┐               │            │</span>
<span class="p">         │  ┌─────┴──────┐  ┌────┴───────┐  ┌────┴───────┐   │</span>
<span class="p">         │  │ p4d.24xl   │  │ p4d.24xl   │  │ p4d.24xl   │   │</span>
<span class="w">         │  └────────────┘  └────────────┘  └────────────┘   │</span>
<span class="w">         └──────────────────────────────────────────────────────┘</span>

<span class="a">         AllReduce (gradient sync) via NCCL over EFA:</span>
<span class="a">         Each node sends/receives gradients to/from ALL others simultaneously</span>
<span class="a">         EFA ensures this completes in microseconds, not milliseconds</span>
`}</AsciiBox>

      <SubTitle>EFA Software Stack</SubTitle>
      <AsciiBox>{`
<span class="w">ML / HPC Application (PyTorch, TensorFlow, OpenFOAM)</span>
<span class="w">              ↓</span>
<span class="p">     NCCL / MPI (collective communication library)</span>
<span class="w">              ↓</span>
<span class="g">     libfabric (EFA provider) — OS-bypass path</span>
<span class="w">              ↓</span>
<span class="c">     EFA Kernel Driver (efa.ko) — control plane setup only</span>
<span class="w">              ↓</span>
<span class="a">     EFA Hardware (p4d, trn1, c5n, etc.)</span>
<span class="w">              ↓</span>
<span class="w">     AWS Network Fabric (within Placement Group)</span>

<span class="w">┌─────────────────────────────────────────────────────┐</span>
<span class="w">│ Note: EFA also supports regular ENA TCP/UDP traffic  │</span>
<span class="w">│ The same ENI can carry both EFA and normal traffic   │</span>
<span class="w">│ EFA OS-bypass only activates for libfabric calls    │</span>
<span class="w">└─────────────────────────────────────────────────────┘</span>
`}</AsciiBox>

      <Callout type="exam" icon="🎓">
        <strong>EXAM QUESTION:</strong> "A machine learning team needs to train a large language model across 100 GPU instances. They're seeing slow training due to network synchronization delays. What should they use?" → <strong>EFA with Cluster Placement Group + p4d instances + NCCL/MPI.</strong>
      </Callout>

      <Callout type="exam" icon="🎓">
        <strong>EXAM TRAP:</strong> EFA is NOT for general low latency. It's specifically for <strong>HPC and ML training workloads</strong> using MPI/NCCL. For general low latency (trading, gaming), ENA Express or Cluster Placement Group with ENA is the answer.
      </Callout>

      <Callout type="tip" icon="💡">
        <strong>OpenShift / K8s HPC:</strong> Use the <code style={{ color: "#A78BFA" }}>aws-efa-k8s-device-plugin</code> DaemonSet to expose EFA devices to pods. Pods request <code style={{ color: "#A78BFA" }}>vpc.amazonaws.com/efa: 1</code> resource. Only one pod per EFA device can use OS-bypass at a time.
      </Callout>
    </div>
  );
}

// ─── COMPARE ─────────────────────────────────────────────────────────────────
function Compare() {
  return (
    <div className="fade-in">
      <SectionTitle color="#F87171">Master Comparison Table</SectionTitle>

      <Table
        accent="#F87171"
        headers={["Property", "Jumbo Frames", "ENI", "ENA", "EFA"]}
        rows={[
          ["What is it", "Large MTU (9001B)", "Virtual NIC", "High-perf driver (SR-IOV)", "RDMA-capable adapter"],
          ["Layer", "Layer 2 (Data Link)", "Layer 2/3", "Driver/HW level", "Transport bypass"],
          ["OS involvement", "Full stack", "Full stack", "Reduced (SR-IOV)", "Bypassed (data plane)"],
          ["Primary benefit", "↓ CPU overhead for bulk data", "IP/MAC identity & flexibility", "↑ Throughput, ↓ Latency", "Ultra-low latency MPI"],
          ["Latency", "No direct improvement", "Standard", "10–25µs", "1–4µs"],
          ["Max bandwidth", "No change to bandwidth", "Up to 100G (via ENA)", "Up to 100 Gbps", "Up to 400+ Gbps"],
          ["Use case", "Storage, big data, analytics", "Multi-homing, failover, licensing", "Web, DB, microservices", "HPC, ML training, MRI/CFD"],
          ["Placement Group", "Recommended", "Not required", "Optional (better perf)", "Required for best perf"],
          ["Instances", "Most with ENA", "All EC2", "Current-gen EC2", "p4d, trn1, c5n, hpc6a"],
          ["K8s relevance", "CNI MTU config", "Pod IP assignment (aws-vpc-cni)", "Node networking", "HPC pod workloads"],
          ["Cost", "Free (config)", "Free (standard ENI)", "Free (driver)", "Higher instance cost"],
        ]}
      />

      <SectionTitle color="#F87171">Decision Flowchart</SectionTitle>
      <AsciiBox>{`
<span class="w">START: What's your networking requirement?</span>
<span class="w">       │</span>
<span class="w">       ├──► Need multiple IPs, failover, or network separation?</span>
<span class="c">       │    └──► Use multiple ENIs (Primary + Secondary)</span>
<span class="w">       │</span>
<span class="w">       ├──► Need high throughput for web/DB/microservices?</span>
<span class="g">       │    └──► ENA (default on current-gen, nothing to do!)</span>
<span class="w">       │</span>
<span class="w">       ├──► Transferring large files within VPC / analytics?</span>
<span class="a">       │    └──► Enable Jumbo Frames (MTU 9001) + ENA</span>
<span class="w">       │</span>
<span class="w">       ├──► Need lowest latency for inter-instance comms?</span>
<span class="g">       │    └──► ENA Express (SRD) + Cluster Placement Group</span>
<span class="w">       │</span>
<span class="w">       └──► HPC / ML training / MPI / NCCL / AllReduce?</span>
<span class="p">            └──► EFA + Cluster Placement Group + p4d/trn1/c5n</span>
<span class="p">                 + libfabric + NCCL/Open MPI</span>

<span class="a">─────────────────────────────────────────────────────────────</span>
<span class="a">THEY STACK: A p4d instance uses ENI + ENA + EFA + Jumbo Frames</span>
<span class="a">            all at the same time for different traffic types!</span>
`}</AsciiBox>

      <SectionTitle color="#F87171">Architecture: How All Four Work Together</SectionTitle>
      <AsciiBox>{`
<span class="w">         p4d.24xlarge (ML Training Node)</span>
<span class="w">┌──────────────────────────────────────────────────────────────────┐</span>
<span class="w">│                                                                  │</span>
<span class="w">│  Applications: PyTorch DDP, NCCL AllReduce, S3 data loading      │</span>
<span class="w">│                │               │                  │              │</span>
<span class="g">│        [NCCL over EFA]  [HTTP/HTTPS (TCP)]  [S3 large files]    │</span>
<span class="w">│                │               │                  │              │</span>
<span class="w">│  ┌─────────────┴──┐  ┌─────────┴──────┐  ┌───────┴───────────┐ │</span>
<span class="p">│  │ EFA Device     │  │ ENI (eth0)     │  │ ENI (eth1)        │ │</span>
<span class="p">│  │ OS-bypass path │  │ ENA driver     │  │ ENA driver        │ │</span>
<span class="p">│  │ MTU: 9001      │  │ MTU: 9001      │  │ MTU: 1500 (mgmt)  │ │</span>
<span class="p">│  │ SG: sg-efa     │  │ SG: sg-app     │  │ SG: sg-ssh        │ │</span>
<span class="p">│  │ 4× 100G links  │  │ 10.0.1.50      │  │ 10.0.2.50         │ │</span>
<span class="w">│  └────────────────┘  └────────────────┘  └───────────────────┘ │</span>
<span class="w">└──────────────────────────────────────────────────────────────────┘</span>
         │                     │                      │
<span class="p">  [EFA fabric]          [VPC fabric]           [Mgmt subnet]</span>
<span class="p">  400 Gbps              100 Gbps               1 Gbps</span>
<span class="p">  1-4µs latency         10-25µs                standard</span>
<span class="a">  Jumbo (9001)          Jumbo (9001)            Standard (1500)</span>
`}</AsciiBox>
    </div>
  );
}

// ─── QUIZ ────────────────────────────────────────────────────────────────────
const QUIZ_QUESTIONS = [
  {
    q: "A company runs a Windows EC2 instance with MAC-bound software licenses. They need to replace the instance. What is the BEST approach to maintain the license?",
    options: [
      "Create an AMI and launch a new instance from it",
      "Detach the secondary ENI and reattach it to the new instance",
      "Use License Manager to transfer the license",
      "Stop and start the instance — the MAC will persist",
    ],
    answer: 1,
    explain: "ENI retains its MAC address when detached and reattached. Attaching the same ENI to the new instance preserves the MAC, keeping the license valid. Stop/start doesn't help — the primary ENI stays but instance might move to different hardware.",
  },
  {
    q: "An HPC team is training a 500B parameter model across 128 p4d.24xlarge instances. After 3 days, they notice the AllReduce gradient synchronization is taking 15% of total training time. What should they implement?",
    options: [
      "Upgrade to higher bandwidth ENA",
      "Enable Jumbo Frames (MTU 9001) on all instances",
      "Enable EFA and use NCCL with libfabric EFA provider",
      "Use a Transit Gateway for inter-instance routing",
    ],
    answer: 2,
    explain: "EFA with NCCL/libfabric enables OS-bypass RDMA-style communication, reducing AllReduce latency from ~15µs to ~1-4µs. p4d instances already have EFA hardware — the fix is enabling it and using NCCL with EFA provider. Jumbo Frames help but won't solve the kernel overhead issue.",
  },
  {
    q: "A developer reports that large file transfers between EC2 instances in different VPCs (same region, peered) fail, but small files and ping work fine. What is the MOST LIKELY cause?",
    options: [
      "VPC Peering doesn't support TCP traffic",
      "Security Groups are blocking port 443",
      "Path MTU Discovery is failing — Jumbo Frame fragmentation needed but ICMP blocked",
      "ENA is not supported for cross-VPC peering",
    ],
    answer: 2,
    explain: "Classic Jumbo Frame / PMTUD issue. Large packets require 'Fragmentation Needed' ICMP messages to negotiate MTU. If Security Groups block ICMP, PMTUD fails, large transfers hang while ping (small packets) works. Fix: allow ICMP type 3 (destination unreachable) in Security Groups.",
  },
  {
    q: "You need an EC2 instance to act as a NAT device, routing traffic between subnets. What ENI configuration is REQUIRED?",
    options: [
      "Attach a second ENI and enable Enhanced Networking",
      "Disable Source/Destination Check on the ENI",
      "Enable Jumbo Frames on the NAT instance's ENI",
      "Assign an Elastic IP to the primary ENI",
    ],
    answer: 1,
    explain: "By default, AWS checks that the source/destination of every packet matches the instance's own IP. A NAT instance forwards packets FROM other IPs — this would be dropped. Disabling Source/Dest Check tells AWS 'this instance may send/receive traffic that isn't addressed to it.'",
  },
  {
    q: "What is the maximum MTU supported within a VPC when using VPC Peering across regions?",
    options: ["9001 bytes", "8500 bytes", "1500 bytes", "4096 bytes"],
    answer: 2,
    explain: "Cross-region VPC Peering is limited to 1500 byte MTU. Jumbo Frames (9001) only work within the same region. Transit Gateway supports up to 8500 bytes. This is a common exam trap — same-region peering = 9001, cross-region peering = 1500.",
  },
  {
    q: "Which of the following correctly describes the relationship between ENA and EFA?",
    options: [
      "EFA replaces ENA for all instance types",
      "EFA is a separate network adapter; instances with EFA also use ENA for regular TCP/UDP traffic",
      "ENA and EFA cannot coexist on the same instance",
      "EFA is only available for storage-optimized instances",
    ],
    answer: 1,
    explain: "EFA provides OS-bypass for MPI/libfabric workloads, but the same network interface also supports standard ENA functionality for TCP/UDP traffic. They coexist — EFA OS-bypass activates only when libfabric/verbs APIs are used; normal traffic still goes through the ENA path.",
  },
  {
    q: "An EKS cluster's worker nodes are t3.medium instances. A team wants to maximize the number of pods per node. They know aws-vpc-cni assigns IPs from secondary ENIs. A t3.medium supports 3 ENIs with 6 IPs each. What is the maximum pod count per node?",
    options: ["18 pods", "17 pods", "15 pods", "16 pods"],
    answer: 1,
    explain: "Max pods = (ENIs × (IPs per ENI − 1)) − 1 + 2. Formula: (3 × 5) − 1 + 2 = 16. Actually: (number of ENIs × (IPv4 per ENI − 1)) + 2 = (3 × (6−1)) + 2 = 15 + 2 = 17. The −1 per ENI accounts for the ENI's own primary IP; the +2 accounts for kube-proxy and aws-node DaemonSet pods.",
  },
];

function Quiz() {
  const [answers, setAnswers] = useState({});
  const [revealed, setRevealed] = useState({});

  const pick = (qi, oi) => {
    if (answers[qi] !== undefined) return;
    setAnswers(a => ({ ...a, [qi]: oi }));
    setRevealed(r => ({ ...r, [qi]: true }));
  };

  const score = Object.keys(answers).filter(k => answers[k] === QUIZ_QUESTIONS[parseInt(k)].answer).length;
  const total = Object.keys(answers).length;

  return (
    <div className="fade-in">
      <SectionTitle color="#FB923C">🎯 AWS SAA Exam Quiz — Networking</SectionTitle>
      <Prose>Test your understanding. Tap an answer — explanations reveal immediately.</Prose>

      {total > 0 && (
        <div style={{ background: "rgba(251,146,60,0.1)", border: "1px solid rgba(251,146,60,0.3)", borderRadius: 10, padding: "12px 16px", marginBottom: 20, display: "flex", alignItems: "center", gap: 16 }}>
          <span style={{ fontSize: 28, fontWeight: 700, color: "#FB923C", fontFamily: "'JetBrains Mono', monospace" }}>{score}/{total}</span>
          <span style={{ color: "#94A3B8", fontSize: 13 }}>answered so far · {QUIZ_QUESTIONS.length - total} remaining</span>
        </div>
      )}

      {QUIZ_QUESTIONS.map((q, qi) => {
        const chosen = answers[qi];
        const isRevealed = revealed[qi];
        return (
          <div key={qi} style={{ background: "#0A1628", border: "1px solid #1E3A5F", borderRadius: 12, padding: 18, marginBottom: 20 }}>
            <div style={{ fontSize: 13, color: "#64748B", fontFamily: "'JetBrains Mono', monospace", marginBottom: 8 }}>Q{qi + 1} of {QUIZ_QUESTIONS.length}</div>
            <div style={{ fontSize: 14, color: "#CBD5E1", fontWeight: 500, lineHeight: 1.6, marginBottom: 14 }}>{q.q}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {q.options.map((opt, oi) => {
                let bg = "#0F1F38", border = "#1E3A5F", color = "#94A3B8";
                if (isRevealed) {
                  if (oi === q.answer) { bg = "rgba(52,211,153,0.1)"; border = "#34D399"; color = "#34D399"; }
                  else if (oi === chosen && chosen !== q.answer) { bg = "rgba(248,113,113,0.1)"; border = "#F87171"; color = "#F87171"; }
                }
                return (
                  <button key={oi} onClick={() => pick(qi, oi)} style={{
                    background: bg, border: `1px solid ${border}`, borderRadius: 8,
                    padding: "10px 14px", textAlign: "left", cursor: isRevealed ? "default" : "pointer",
                    color, fontSize: 13, lineHeight: 1.5, transition: "all 0.2s",
                    fontFamily: "'Inter', sans-serif"
                  }}>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", marginRight: 10, opacity: 0.5 }}>{String.fromCharCode(65 + oi)}.</span>
                    {opt}
                    {isRevealed && oi === q.answer && " ✓"}
                    {isRevealed && oi === chosen && chosen !== q.answer && " ✗"}
                  </button>
                );
              })}
            </div>
            {isRevealed && (
              <div style={{ marginTop: 14, background: "rgba(0,212,255,0.07)", border: "1px solid rgba(0,212,255,0.2)", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#7DD3FC", lineHeight: 1.6 }}>
                <span style={{ color: "#00D4FF", fontWeight: 600 }}>Explanation: </span>{q.explain}
              </div>
            )}
          </div>
        );
      })}

      {total === QUIZ_QUESTIONS.length && (
        <div style={{ background: "rgba(251,146,60,0.1)", border: "2px solid #FB923C", borderRadius: 12, padding: 24, textAlign: "center" }}>
          <div style={{ fontSize: 32, fontWeight: 700, color: "#FB923C", marginBottom: 8 }}>{score}/{QUIZ_QUESTIONS.length}</div>
          <div style={{ color: "#94A3B8", fontSize: 14 }}>
            {score === QUIZ_QUESTIONS.length ? "🏆 Perfect! You're ready for the exam." :
             score >= 5 ? "💪 Strong performance! Review the ones you missed." :
             "📚 Good effort — revisit the topic tabs and try again."}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── MAIN APP ────────────────────────────────────────────────────────────────
export default function AWSNetworking() {
  const [active, setActive] = useState("jumbo");

  const sections = {
    jumbo: <JumboFrames />,
    eni: <ENI />,
    ena: <ENA />,
    efa: <EFA />,
    compare: <Compare />,
    quiz: <Quiz />,
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: FONT_IMPORT }} />
      <div style={layout.app}>
        <div style={layout.sidebar}>
          <div style={{ padding: "20px 16px 12px", borderBottom: `1px solid ${COLORS.border}` }}>
            <div style={{ fontSize: 11, color: COLORS.accent, fontWeight: 700, textTransform: "uppercase", letterSpacing: 2, marginBottom: 4 }}>
              AWS
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>Advanced Networking</div>
            <div style={{ display: "flex", gap: 4, marginTop: 8, flexWrap: "wrap" }}>
              {["SAA-C03", "Networking"].map((t) => (
                <span key={t} style={layout.badge(COLORS.accent)}>{t}</span>
              ))}
            </div>
          </div>
          <nav style={{ padding: "10px 0" }}>
            {TABS.map((tab) => (
              <div key={tab.id} style={layout.navItem(active === tab.id, tab.color)} onClick={() => setActive(tab.id)}>
                <span style={{ fontSize: 16 }}>{tab.icon}</span>
                <span>{tab.label}</span>
              </div>
            ))}
          </nav>
          <div style={{ padding: "12px 16px", borderTop: `1px solid ${COLORS.border}` }}>
            <div style={{ fontSize: 11, color: COLORS.textDim, lineHeight: 1.6 }}>
              Instance families:<br /><code style={{ color: COLORS.textMuted, fontFamily: FONT }}>p4d · trn1 · c5n · hpc6a</code>
            </div>
          </div>
        </div>
        <div style={layout.main}>{sections[active]}</div>
      </div>
    </>
  );
}
